import { Link } from "react-router-dom";
import {
  ArrowRight,
  Home,
  Hammer,
  Map,
  Calculator,
  Briefcase,
  ArrowUpRight,
} from "lucide-react";
import { Container } from "../layout/Container.jsx";

const ICON_MAP = {
  home: Home,
  hammer: Hammer,
  map: Map,
  calculator: Calculator,
  briefcase: Briefcase,
};

/**
 * Continuously sliding services marquee — shows every service we build,
 * scrolling left at a calm pace. Side-fade masks keep the edges clean,
 * hover pauses the scroll so the visitor can read.
 */
export function FeaturedServices({ services = [] }) {
  if (services.length === 0) return null;

  // Duplicate so the CSS marquee loop is seamless. We need at least 2 cycles
  // so the second copy is in place by the time the first scrolls off.
  const loop = [...services, ...services];

  return (
    <section className="border-t border-border-subtle bg-bg-elevated py-20 lg:py-28">
      <Container>
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="eyebrow">What we build</p>
            <h2 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
              Services across the journey
            </h2>
            <p className="mt-3 text-text-secondary">
              From the first sketch to the 12-month aftercare visit — every service we offer,
              under one builder.
            </p>
          </div>
          <Link
            to="/services"
            className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover"
          >
            All services <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>

      {/* Marquee — overflow-hidden + side-fade mask so the edges never look chopped. */}
      <div
        className="group relative overflow-hidden"
        style={{
          maskImage:
            "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          WebkitMaskImage:
            "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
        }}
      >
        <div
          className="flex w-max gap-6 px-4 py-3 group-hover:[animation-play-state:paused] motion-reduce:[animation-name:none]"
          style={{ animation: `marquee ${Math.max(28, services.length * 7)}s linear infinite` }}
        >
          {loop.map((s, i) => (
            <ServiceCard key={`${s._id ?? s.slug}-${i}`} service={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service }) {
  const Icon = ICON_MAP[service.icon] ?? Home;
  return (
    <Link
      to={`/services/${service.slug}`}
      className="group/card flex h-full w-[300px] shrink-0 flex-col rounded-2xl border border-border-subtle bg-bg-base p-6 transition-all hover:-translate-y-0.5 hover:border-accent hover:shadow-card-hover sm:w-[340px] lg:w-[380px] lg:p-7"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-accent/15 text-accent">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-5 font-display text-2xl text-text-primary group-hover/card:text-accent">
        {service.title}
      </h3>
      {service.shortDescription && (
        <p className="mt-2 line-clamp-3 text-sm text-text-secondary">
          {service.shortDescription}
        </p>
      )}
      <span className="mt-auto pt-6 text-sm text-accent">
        <span className="inline-flex items-center gap-1 transition-transform group-hover/card:translate-x-1">
          Learn more <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </span>
    </Link>
  );
}
